import React, { useMemo } from 'react';
import { Button, Modal, Popover, Spin } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { BatchPushAreaRow, BatchPushPreview } from '../graphql/batchPush';
import { formatNumber, PreviewChangeSide, summarizePreviewChanges } from '../batchPushUtils';
import PushBatchTable from './PushBatchTable';

interface BatchPushConfirmModalProps {
    open: boolean;
    /** 前置比对加载中 */
    loading: boolean;
    preview: BatchPushPreview | null;
    /** 看板区域：变化明细中的区域名与排序 */
    areas: BatchPushAreaRow[];
    submitting: boolean;
    onBack: () => void;
    onConfirm: () => void;
}

/** 「涉及 X 个区域」中可点击的区域数，点开浮层列出各区域件数（PRD 交互稿） */
const AreaCountPopover: React.FC<{ side: PreviewChangeSide; title: string; testId: string }> = ({ side, title, testId }) => {
    const { t } = useTranslation();
    const content = (
        <div className="min-w-[180px] max-h-64 overflow-auto">
            {side.areas.map((a) => (
                <div key={a.areaId} className="flex justify-between gap-8 py-1">
                    <span>{a.areaName}</span>
                    <b>{t('wms.batchPush.confirm.areaParcelCount', { num: formatNumber(a.count) })}</b>
                </div>
            ))}
        </div>
    );
    return (
        <Popover trigger="click" title={title} content={content}>
            <button type="button" className="text-indigo-600 underline font-bold px-0.5" data-testid={testId}>
                {side.areas.length}
            </button>
        </Popover>
    );
};

/**
 * 确认推送弹框。打开时已调用一次前置比对（previewBatchPush），这是唯一的一次比对（Q10、Q36）：
 * 统计第二行展示勾选后因运单状态变化导致的增加 / 剔除件数及涉及区域（为 0 的半句不展示）；
 * 弹框内点「确认推送」直接提交，不再比对，按单元格推送库中最新数据。
 */
const BatchPushConfirmModal: React.FC<BatchPushConfirmModalProps> = ({
    open,
    loading,
    preview,
    areas,
    submitting,
    onBack,
    onConfirm,
}) => {
    const { t } = useTranslation();
    const total = preview?.totalCount ?? 0;
    const rows = (preview?.batches ?? [])
        .filter((b) => (b.parcelCount ?? 0) > 0)
        .map((b) => ({
            key: b.wmsBatchNumber ?? '',
            column: b,
            count: b.parcelCount ?? 0,
            areaNames: b.areaNames ?? [],
        }));
    const changes = useMemo(() => summarizePreviewChanges(preview?.cells, areas), [preview, areas]);
    const changeParts: React.ReactNode[] = [];
    if (changes.added.total > 0) {
        changeParts.push(
            <span key="added">
                {t('wms.batchPush.confirm.changeAdded', { num: formatNumber(changes.added.total) })}
                <AreaCountPopover side={changes.added} title={t('wms.batchPush.confirm.addedAreasTitle')} testId="added-areas" />
                {t('wms.batchPush.confirm.changeAreaUnit')}
            </span>,
        );
    }
    if (changes.removed.total > 0) {
        if (changeParts.length > 0) changeParts.push(<span key="sep">{t('wms.batchPush.confirm.changeSeparator')}</span>);
        changeParts.push(
            <span key="removed">
                {t('wms.batchPush.confirm.changeRemoved', { num: formatNumber(changes.removed.total) })}
                <AreaCountPopover side={changes.removed} title={t('wms.batchPush.confirm.removedAreasTitle')} testId="removed-areas" />
                {t('wms.batchPush.confirm.changeAreaUnit')}
            </span>,
        );
    }
    return (
        <Modal
            open={open}
            width={760}
            title={
                <span className="flex items-center gap-2">
                    <ExclamationCircleFilled className="text-orange-500" />
                    {t('wms.batchPush.confirm.title')}
                </span>
            }
            onCancel={onBack}
            maskClosable={false}
            footer={[
                <Button key="back" onClick={onBack} disabled={submitting}>
                    {t('wms.batchPush.confirm.back')}
                </Button>,
                <Button key="ok" type="primary" loading={submitting} disabled={loading || total <= 0} onClick={onConfirm}>
                    {t('wms.batchPush.confirm.ok')}
                </Button>,
            ]}
        >
            <Spin spinning={loading}>
                <div className="text-red-600 font-medium mb-3">{t('wms.batchPush.confirm.warning')}</div>
                <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 mb-3 leading-7">
                    {/* 统计数字加粗（设计稿）：共推送 / 回仓重派 / 新到仓 */}
                    <div data-testid="confirm-summary">
                        <Trans
                            i18nKey="wms.batchPush.confirm.summary"
                            values={{
                                total: formatNumber(total),
                                returnRedispatch: formatNumber(preview?.returnRedispatchCount),
                                newArrival: formatNumber(preview?.newArrivalCount),
                            }}
                            components={{ b: <b /> }}
                        />
                    </div>
                    {changeParts.length > 0 && (
                        <div data-testid="confirm-changes">
                            {t('wms.batchPush.confirm.changePrefix')}
                            {changeParts}
                            {t('wms.batchPush.confirm.changeEnd')}
                        </div>
                    )}
                </div>
                {total > 0 ? (
                    <div className="max-h-80 overflow-auto">
                        <PushBatchTable rows={rows} />
                    </div>
                ) : (
                    !loading && <div className="text-gray-500 py-6 text-center">{t('wms.batchPush.confirm.empty')}</div>
                )}
            </Spin>
        </Modal>
    );
};

export default BatchPushConfirmModal;
